import React, {
    createContext,
    useState,
    useContext,
    useEffect,
    ReactNode,
    FC,
} from 'react';
import {
    EventType,
    PublicClientApplication,
    AccountInfo,
    AuthenticationResult,
    InteractionRequiredAuthError,
    EventMessage,
} from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from '../authConfig';
import {jwtDecode} from "jwt-decode";


interface AuthContextType {
    cdsid: string | null;
    bearerToken: string | null;
    paCode : string | null;
    email : string | null;
    jwtRoles: string[];
}

/** Shape of the decoded JWT access token */
interface TokenPayload {
    email?: string;
    paCode?: string;
    /** Azure AD app roles assigned to the user (e.g. "FSR:FSRAdministrator") */
    roles?: string[];
}

interface UserLogInfo {
    emailId: string;
    countryCode: string;
    paCode: string;
    vendorCode: string;
    createdId: string;
    createdTime: string;
    lastUpdatedUser: string;
    lastUpdatedTime: string;
}

const apiScope: string[] = [
   // `api://${process.env.REACT_APP_BACKEND_APP_CLIENT_ID || '9a14c85e-700b-44a5-be86-1107d3fc0e5e'}/DCDS-FAD-SRS-UI`,
    `api://${process.env.REACT_APP_BACKEND_APP_CLIENT_ID}/DCDS-FAD-SRS-UI`,
];

//console.log("apiScope : " + apiScope);

const msalInstance: PublicClientApplication = new PublicClientApplication(msalConfig);

const AuthContext = createContext<AuthContextType>({
    cdsid: null,
    bearerToken: null,
    paCode : null,
    email : null,
    jwtRoles: [],
});

const logUserLogin = async (account: AccountInfo, token: string | null = null): Promise<void> => {

    // Add this guard clause at the very top
    if (!token) {
        console.warn("Skipping user log: No token available yet.");
        return;
    }
    //console.log("bearer Token: "+token);
    try {
        const now = new Date();
        const offset = now.getTimezoneOffset() * 60 * 1000;
        const timestampWithoutZ = new Date(now.getTime() - offset).toISOString().slice(0, -1);
        const username = account.username;
        const name = account.name || 'Unknown';
        const cdsid = username.split('@')[0].toUpperCase();

       // const [paCode, setPaCode] = useState<string | null>(null);

        //const paCode = username.

        const userLogInfo: UserLogInfo = {
            emailId: username,
            countryCode: "",
            paCode: "",
            vendorCode: "",
            createdId: cdsid,
            createdTime: timestampWithoutZ,
            lastUpdatedUser: cdsid,
            lastUpdatedTime: timestampWithoutZ,
        };
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        //const apiUrl = process.env.REACT_APP_FADSRS_API_URL || 'https://fadsrs-dcds-api-dev-793675442456.us-central1.run.app/fadsrs';
        const apiUrl = process.env.REACT_APP_FADSRS_API_URL ;
        //console.log("apiUrl: " + apiUrl);
        if (apiUrl) {
            const response = await fetch(`${apiUrl}/userinfo`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(userLogInfo),
            });

            if (!response.ok) {
                throw new Error(`Failed to log user info: ${response.status} ${response.statusText}`);
            }
        }
    } catch (error) {
        if (error instanceof Error) {
            console.error('Error logging user info:', error.message);
        } else {
            console.error('An unknown error occurred during user info logging:', error);
        }
    }
};

const updateUserActivity = async (cdsid: string, token: string, paCode: string | null): Promise<void> => {
    try {
        const apiUrl = process.env.REACT_APP_API_BASE_URL || '';
        if (!apiUrl) {
            return;
        }
console.log("Updating user activity for CDSID:", cdsid, "with PA Code:", paCode);
        const updateData = {
            paCode : paCode,
            lastUpdatedUser: cdsid,
            lastUpdatedTime: new Date().toISOString()
        };

        const response = await fetch(`${apiUrl}/api/user-logs/activity`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ cdsid, ...updateData })
        });

        if (!response.ok) {
            return;
        }
    } catch (error) {
        if (error instanceof Error) {
            console.error('Error updating user activity:', error.message);
        } else {
            console.error('An unknown error occurred during user activity update:', error);
        }
    }
};

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
    const [cdsid, setCdsid] = useState<string | null>(null);
    const [bearerToken, setBearerToken] = useState<string | null>(null);
    const [paCode, setPaCode] = useState<string | null>(null);
    const [email, setEmail] = useState<string | null>(null);
    const [jwtRoles, setJwtRoles] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [msalInitialized, setMsalInitialized] = useState<boolean>(false);
    const [tokenExpirationTime, setTokenExpirationTime] = useState<Date | null>(null);
    const setActiveAccountAndCdsid = async (account: AccountInfo): Promise<void> => {
        msalInstance.setActiveAccount(account);
        const userCdsid = account.username.split('@')[0].toUpperCase();
        setCdsid(userCdsid);

        const token = await acquireToken(apiScope);

        if (token) {
            try {
                const decoded = jwtDecode<TokenPayload>(token);

                const emailId = decoded.email ?? null;
                setEmail(emailId);
                const paCodeValue = decoded.paCode ?? null;
                setPaCode(paCodeValue);
                // Extract roles from JWT — primary source for access control
                const roles = Array.isArray(decoded.roles) ? decoded.roles : [];
                setJwtRoles(roles);

            } catch (error) {
                console.error("Failed to decode token:", error);
                setJwtRoles([]);
            }
            await updateUserActivity(userCdsid, token, paCode);
        }
    };

    const acquireToken = async (scopes: string[]): Promise<string | null> => {
        const activeAccount = msalInstance.getActiveAccount();
        if (!activeAccount) {
            return null;
        }

        try {
            const response: AuthenticationResult | null = await msalInstance.acquireTokenSilent({
                scopes: scopes,
                account: activeAccount,
            });

            if (response && response.accessToken) {
                setBearerToken(response.accessToken);
                setTokenExpirationTime(response.expiresOn);
                return response.accessToken;
            } else {
                return null;
            }
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                try {
                    await msalInstance.acquireTokenRedirect({ scopes: scopes });
                    return null;
                } catch (redirectError) {
                    return null;
                }
            } else {
                return null;
            }
        }
    };

    useEffect(() => {
        let timeoutId: NodeJS.Timeout | null = null;

        if (tokenExpirationTime) {
            const now = new Date().getTime();
            const expiryTime = tokenExpirationTime.getTime();
            const timeUntilExpiry = expiryTime - now;

            const logoutBuffer = 30 * 1000;

            if (timeUntilExpiry > logoutBuffer) {
                timeoutId = setTimeout(async () => {
                    const account = msalInstance.getActiveAccount();
                    const currentToken = bearerToken;


                    if (account && currentToken) {
                        await logUserLogin(account, currentToken);
                    }
                    msalInstance.logoutRedirect();
                }, timeUntilExpiry - logoutBuffer);

            } else {
                const account = msalInstance.getActiveAccount();
                const currentToken = bearerToken;
                if (account && currentToken) {
                    (async () => {
                        await logUserLogin(account, currentToken);
                        msalInstance.logoutRedirect();
                    })();
                } else {
                    msalInstance.logoutRedirect();
                }
            }
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [tokenExpirationTime, bearerToken]);

    useEffect(() => {
        let activityInterval: NodeJS.Timeout | null = null;

        if (cdsid && bearerToken) {
            activityInterval = setInterval(() => {
                updateUserActivity(cdsid, bearerToken, paCode);
            }, 30 * 60 * 1000);
        }

        return () => {
            if (activityInterval) {
                clearInterval(activityInterval);
            }
        };
    }, [cdsid, bearerToken]);

    useEffect(() => {
        let isMounted = true;

        async function initializeMsal() {
            try {
                await msalInstance.initialize();
                if (!isMounted) return;

                msalInstance.addEventCallback(async (event: EventMessage) => {
                    if (
                        event.eventType === EventType.LOGIN_SUCCESS &&
                        event.payload
                    ) {
                        const payload = event.payload as AuthenticationResult;
                        if (payload.account) {
                            const account: AccountInfo = payload.account;
                            let tokenForLog: string | null = payload.accessToken;

                            if (!tokenForLog) {
                                tokenForLog = await acquireToken(apiScope);
                            }

                           // await logUserLogin(account, tokenForLog);
                            await setActiveAccountAndCdsid(account);
                        }
                    } else if (event.eventType === EventType.LOGIN_FAILURE) {
                        setLoading(false);
                    }
                });

                const tokenResponse: AuthenticationResult | null = await msalInstance.handleRedirectPromise();

                if (tokenResponse && tokenResponse.account) {
                    await setActiveAccountAndCdsid(tokenResponse.account);
                } else {
                    const accounts: AccountInfo[] = msalInstance.getAllAccounts();

                    if (accounts.length === 0) {
                        await msalInstance.loginRedirect({ scopes: apiScope });
                    } else {
                        const account = accounts[0];
                        const token = await acquireToken(apiScope);
                        await logUserLogin(account, token);
                        await setActiveAccountAndCdsid(account);
                    }
                }
            } catch (error) {
            } finally {
                if (isMounted) {
                    setMsalInitialized(true);
                    setLoading(false);
                }
            }
        }

        initializeMsal();

        return () => {
            isMounted = false;
        };
    }, []);

    if (loading) {
        return <div>Loading Authentication...</div>;
    }

    return (
        <AuthContext.Provider value={{ cdsid, bearerToken, paCode, email, jwtRoles }}>
            <MsalProvider instance={msalInstance}>
                {children}
            </MsalProvider>
        </AuthContext.Provider>
    );
};

export const useAuthContext = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    return context;
};

export const useCdsid = (): string | null => {
    const { cdsid } = useAuthContext();
    return cdsid;
};

export const useBearerToken = (): string | null => {
    const { bearerToken } = useAuthContext();
    return bearerToken;
};

export const usePaCode = (): string | null => {
    const { paCode } = useAuthContext();
    return paCode;
};

export const useJwtRoles = (): string[] => {
    const { jwtRoles } = useAuthContext();
    return jwtRoles;
};